import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CircleAlert, CircleCheck, LoaderCircle, PlugZap, TriangleAlert, X } from 'lucide-react';
import { useId, useRef, useState, type ComponentType, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, ApiProblem, type IntegrationSwitchOverview, type NoticesResponse, type SwitchMode } from '../lib/api.ts';
import { useScreen } from '../lib/layout.ts';
import { useModalFocus } from '../lib/modal-focus.ts';
import { useSession } from '../lib/session.tsx';
import { noticeMeta, noticeTitle, silentOnly, switchWords, SYSTEM_STATUS_TEXTS as T, systemStatus, type SystemStatusLevel } from '../lib/system-status.ts';
import { cn } from '../lib/utils.ts';
import { StatusPill } from './ui/status-pill.tsx';
import { IconCircle, TILE_TITLE_CLASS } from './ui/tile.tsx';
import { SWITCHES_PATH } from '../settings-pages.ts';

const MODE_DOT: Record<SwitchMode, string> = {
  live: 'bg-ready',
  shadow: 'bg-check-dot',
  off: 'bg-ink-3',
};
const LEVEL_DOT: Record<SystemStatusLevel, string> = {
  ok: 'bg-ready',
  warn: 'bg-check-dot',
  error: 'bg-clarify',
};
const LEVEL_ICON = {
  ok: CircleCheck,
  warn: CircleAlert,
  error: TriangleAlert,
} as const;
/** Pille je Schnittstelle (Entwurf A04, Ticket 1.243): Live grün, Probe bernstein, Aus grau – immer mit Wort. */
const MODE_PILL = {
  live: { tone: 'ready', text: 'Live' },
  shadow: { tone: 'check', text: 'Probe – geht nicht hinaus' },
  off: { tone: 'neutral', text: 'Aus' },
} as const satisfies Record<SwitchMode, { tone: 'ready' | 'check' | 'neutral'; text: string }>;
const PANEL_TEXTS = {
  readOnly: 'nur lesen',
  footer: 'Nur lesen – Schalter ändert nur der Admin.',
} as const;

/** Gleiche Abfragen wie bisher die Startseiten-Karte (Schalter alle 60 s, Hinweise alle 30 s); seit Story 1.115 die einzige Anzeige der Störungen. */
function useSystemStatusData() {
  const { getToken } = useSession();
  const switches = useQuery({
    queryKey: ['integration-switches', 'status'],
    queryFn: () => apiFetch<IntegrationSwitchOverview>(getToken, '/api/v1/integration-switches'),
    refetchInterval: 60_000,
    retry: false,
  });
  const notices = useQuery({
    queryKey: ['notices'],
    queryFn: () => apiFetch<NoticesResponse>(getToken, '/api/v1/notices'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const pending = switches.isPending || notices.isPending;
  const status = systemStatus({
    counterparts: switches.data?.counterparts ?? [],
    notices: notices.data?.items ?? [],
    silent: notices.data?.silent ?? [],
    failed: Boolean(switches.error || notices.error),
  });
  // Zahl der Störungen am roten Punkt (Story 1.115): je Gegenstelle eine – nicht erreichbar oder meldet sich nicht.
  const items = notices.data?.items ?? [];
  const disturbances = items.length + silentOnly(items, notices.data?.silent ?? []).length;
  return { switches, notices, pending, status, disturbances };
}

type Data = ReturnType<typeof useSystemStatusData>;

function StatusBody({ data, onNavigate }: { data: Data; onNavigate: () => void }) {
  const items = data.switches.data?.counterparts ?? [];
  const notices = data.notices.data?.items ?? [];
  // Je Gegenstelle ein Eintrag: nicht erreichbar geht vor „meldet sich nicht“.
  const silent = silentOnly(notices, data.notices.data?.silent ?? []);
  const error = data.switches.error ?? data.notices.error;
  const Icon = LEVEL_ICON[data.status.level];
  return (
    <div className="mt-2 flex flex-col gap-4 px-2">
      {data.pending ? (
        <p className="type-meta text-ink-3">Lädt …</p>
      ) : (
        <p data-testid="systemstatus-satz" className="flex items-center gap-2 text-row text-ink">
          <span aria-hidden className={cn('size-2.5 flex-none rounded-full', LEVEL_DOT[data.status.level])} />
          <Icon size={16} strokeWidth={1.5} aria-hidden className="flex-none text-ink-2" />
          {data.status.sentence}
        </p>
      )}
      {error ? <p className="type-meta text-clarify">{error instanceof ApiProblem ? error.detail : 'Etwas ist schiefgelaufen.'}</p> : null}
      {notices.length + silent.length > 0 ? (
        <ul aria-label={T.disturbances} className="m-0 flex list-none flex-col gap-1 p-0">
          {notices.map((n) => (
            <li key={n.counterpart} data-testid="systemstatus-stoerung" className="text-row">
              <span className="block text-clarify">{noticeTitle(n)}</span>
              <span className="type-meta block text-ink-3">{noticeMeta(n)}</span>
            </li>
          ))}
          {silent.map((s) => (
            <li key={`silent-${s.counterpart}`} data-testid="systemstatus-still" className="text-row text-clarify">
              {s.text}
            </li>
          ))}
        </ul>
      ) : null}
      {items.length > 0 ? (
        <section aria-label={T.switchesHeading}>
          <h3 className="m-0 text-body font-medium text-ink-2">{T.switchesHeading} (lesen/senden)</h3>
          <ul aria-label="Schnittstellen" className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
            {items.map((c) => {
              const { main, words } = switchWords(c);
              return (
                <li key={c.id} className="flex min-w-0 items-center gap-space-3 border-t border-line py-space-2 first:border-t-0">
                  <span aria-hidden className={cn('size-2 flex-none rounded-full', MODE_DOT[main])} />
                  <span className="min-w-0 flex-1 text-body leading-tight">
                    <span className="block text-ink">{c.label}</span>
                    <span className="type-meta block text-ink-3">{words}</span>
                  </span>
                  <StatusPill tone={MODE_PILL[main].tone}>{MODE_PILL[main].text}</StatusPill>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-space-2 border-t border-line pt-space-2">
        <span className="type-meta text-ink-3">{PANEL_TEXTS.footer}</span>
        <Link
          to={SWITCHES_PATH}
          onClick={onNavigate}
          className="inline-flex min-h-11 items-center gap-space-2 rounded-full text-row font-medium text-brand hover:underline"
        >
          {T.switchesLink}
          <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/** Handy: Blatt von unten (Story 1.98); Fokus auf die Überschrift, bleibt im Blatt; „Schließen“, Esc oder Tipp daneben schließt. */
function PhoneSheet({
  opener,
  onClose,
  data,
  headingId,
  panelId,
}: {
  opener: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  data: Data;
  headingId: string;
  panelId: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useModalFocus(panel, opener, onClose, () => heading.current);
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div aria-hidden className="absolute inset-0 bg-scrim" onClick={onClose} />
      <div
        ref={panel}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="systemstatus-blatt"
        className="relative max-h-sheet-max overflow-y-auto rounded-t-card bg-card px-4 pt-4 pb-6 shadow-float"
      >
        <div className="flex items-center gap-3">
          <h2 id={headingId} ref={heading} tabIndex={-1} className="type-card-title min-w-0 flex-1 px-2">
            {T.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 flex-none cursor-pointer items-center rounded-full bg-secondary px-4 text-row text-secondary-foreground"
          >
            {T.close}
          </button>
        </div>
        <StatusBody data={data} onNavigate={onClose} />
      </div>
    </div>,
    document.body,
  );
}

/**
 * Desktop/Tablet: Seitenpanel rechts über abgedunkelter Seite (Entwurf A04, Ticket 1.243); modal wie das Handy-Blatt –
 * Fokus auf die Überschrift, Fokusfalle, Esc, „Schließen“ oder Klick auf die Abdunklung schließt, Fokus zurück zum Knopf.
 * Im Portal, weil die Kopfzeile (Glas mit Unschärfe) sonst der Bezugsrahmen von `fixed` wäre.
 */
function DesktopPanel({
  opener,
  onClose,
  data,
  headingId,
  panelId,
  Icon,
}: {
  opener: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  data: Data;
  headingId: string;
  panelId: string;
  Icon: ComponentType<{ size?: number; strokeWidth?: number }>;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useModalFocus(panel, opener, onClose, () => heading.current);
  return createPortal(
    <>
      <div aria-hidden data-testid="systemstatus-abdunklung" className="fixed inset-0 z-40 bg-scrim" onClick={onClose} />
      <div
        ref={panel}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="systemstatus-panel"
        className="fixed right-space-4 bottom-space-4 left-space-4 z-50 flex flex-col overflow-y-auto rounded-card bg-card px-space-3 pb-space-4 shadow-panel tablet:left-auto tablet:w-panel-w"
        style={{ top: 'calc(var(--banner-h, 0px) + var(--space-4))' }}
      >
        <div className="flex flex-none items-center gap-space-3 px-space-1 pt-space-4 pb-space-2">
          <IconCircle>
            <Icon size={18} strokeWidth={1.5} />
          </IconCircle>
          <div className="min-w-0 flex-1">
            <h2 id={headingId} ref={heading} tabIndex={-1} className={TILE_TITLE_CLASS}>
              {T.title}
            </h2>
            <p className="type-meta text-ink-3">{PANEL_TEXTS.readOnly}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={T.close}
            title={T.close}
            className="grid size-icon-circle flex-none cursor-pointer place-items-center rounded-full bg-fill text-ink-2 hover:text-ink"
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
        <StatusBody data={data} onNavigate={onClose} />
      </div>
    </>,
    document.body,
  );
}

/**
 * Systemstatus in der Kopfzeile (Rudolf 12.10.2026, Story 1.98; DESIGN „Design S3 › Kopfzeile“, EXPERIENCE §2.4):
 * runder 44-px-Knopf links neben der Glocke mit Farbpunkt und Symbolform, Satz als Tooltip und Name für Screenreader.
 * Klick öffnet ein Panel wie die Glocke (Handy: Blatt von unten) mit Störungen, Schalter-Stellung je Schnittstelle und
 * Link zu den Betriebsschaltern. Nur lesen.
 */
export function SystemStatusButton() {
  const data = useSystemStatusData();
  const [open, setOpen] = useState(false);
  const phone = useScreen() === 'phone';
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const headingId = useId();

  const sentence = data.pending ? T.loading : data.status.sentence;
  const level = data.pending ? null : data.status.level;
  const Icon = level ? LEVEL_ICON[level] : data.pending ? LoaderCircle : PlugZap;
  const close = () => setOpen(false);
  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${T.title}: ${sentence}`}
        title={sentence}
        data-testid="systemstatus"
        data-level={level ?? 'pending'}
        className={cn(
          'relative grid flex-none cursor-pointer place-items-center rounded-full text-ink-2 hover:text-ink',
          phone ? 'size-11' : 'size-10 transition-colors duration-150 hover:bg-hover pointer-coarse:size-11',
        )}
      >
        <Icon size={18} strokeWidth={1.5} aria-hidden />
        {level === 'error' && data.disturbances > 0 ? (
          // Rot mit Zahl der Störungen (Story 1.115, [RUDOLF 13.10.2026]); Text in `on-ink` auf `clarify` (hell und dunkel ≥ 4,5 : 1).
          <span
            aria-hidden
            data-testid="systemstatus-zahl"
            className="absolute top-badge-inset right-badge-inset grid h-4 min-w-4 place-items-center rounded-full bg-clarify px-1 text-2xs leading-none font-semibold text-on-ink ring-2 ring-card"
          >
            {data.disturbances}
          </span>
        ) : level ? (
          <span aria-hidden className={cn('absolute top-dot-inset right-dot-inset size-2.5 rounded-full ring-2 ring-card', LEVEL_DOT[level])} />
        ) : null}
      </button>
      {open && phone ? <PhoneSheet opener={button} onClose={close} data={data} headingId={headingId} panelId={panelId} /> : null}
      {open && !phone ? <DesktopPanel opener={button} onClose={close} data={data} headingId={headingId} panelId={panelId} Icon={Icon} /> : null}
    </div>
  );
}
